import type { Conversation, Message, Paginator } from '@twilio/conversations';
import type { IMessage, AvatarProps, BubbleProps, SystemMessageProps } from 'react-native-gifted-chat';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { ActivityIndicator, Platform } from 'react-native';

import { useIsFocused } from '@react-navigation/native';
import { GiftedChat, Bubble } from 'react-native-gifted-chat';
import { YStack } from 'tamagui';

import { Toast } from '@shared/utils';
import { BodyS, LabelM } from '@south-street-app/atoms';
import { InitialsCircle } from '@south-street-app/molecules';
import { useChatStore } from '@south-street-app/stores';
import {
  getConversationAttributes,
  isVisibleConversationMessage,
  markConversationRead,
} from '@south-street-app/utils';
import { useGetCurrentEndUser } from '@utility-nyc/react-query-sdk';

import { ChatInput } from '../ChatScreen/ChatInput';
import { ChatSendButton } from '../ChatScreen/ChatSendButton';

const PAGE_SIZE = 30;
const CHAT_FONT_FAMILY = Platform.OS === 'web' ? 'Inter' : 'InterRegular';

const toGiftedChatMessage = (
  message: Message,
  conversation: Conversation | null,
): IMessage | null => {
  if (
    !isVisibleConversationMessage(message.author, message.body, conversation)
  ) {
    return null;
  }

  return {
    _id: message.sid,
    text: message.body ?? '',
    createdAt: message.dateCreated ?? new Date(),
    user: {
      _id: message.author ?? '',
      name: message.author ?? '',
    },
  };
};

const mergeMessages = (messages: IMessage[]) =>
  [...messages].sort(
    (left, right) =>
      new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
  );

const RESOLVED_MESSAGE_ID = 'resolved-indicator';
const CHAT_TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
});

const ResolvedSystemMessage = (_props: SystemMessageProps<IMessage>) => (
  <YStack alignItems={'center'} paddingVertical={'$3'}>
    <BodyS color={'$mono500'}>{"Marked as resolved"}</BodyS>
  </YStack>
);

const GenericChatAvatar = (
  props: AvatarProps<IMessage>,
  avatarName = 'South Street',
) => {
  if (props.position !== 'left') {
    return null;
  }

  return (
    <YStack
      marginRight={'$2'}
      testID={
        props.currentMessage._id
          ? `customer-chat-message-avatar-${props.currentMessage._id}`
          : undefined
      }
    >
      <InitialsCircle name={avatarName} size={'small'} usePadding={false} />
    </YStack>
  );
};

const formatChatTimestamp = (createdAt: IMessage['createdAt']) => {
  if (!createdAt) {
    return '';
  }

  return CHAT_TIME_FORMATTER.format(new Date(createdAt));
};

const GenericChatBubble = (props: BubbleProps<IMessage>) => {
  const createdAt = props.currentMessage.createdAt;
  const messageId = props.currentMessage._id;
  const position = props.position;

  return (
    <YStack
      alignItems={position === 'right' ? 'flex-end' : 'flex-start'}
      testID={messageId ? `customer-chat-message-${messageId}` : undefined}
    >
      <Bubble
        {...props}
        containerStyle={{
          left: { marginRight: 48 },
          right: { marginLeft: 48 },
        }}
        renderTime={() => null}
        textStyle={{
          left: { color: '#0E1116', fontFamily: CHAT_FONT_FAMILY },
          right: { color: '#FFFFFF', fontFamily: CHAT_FONT_FAMILY },
        }}
        wrapperStyle={{
          left: {
            backgroundColor: '#FFFFFF',
            borderRadius: 20,
            paddingHorizontal: 16,
            paddingVertical: 12,
          },
          right: {
            backgroundColor: '#1B3B8C',
            borderRadius: 20,
            paddingHorizontal: 16,
            paddingVertical: 12,
          },
        }}
      />
      {createdAt ? (
        <BodyS
          color={position === 'right' ? '$mono200' : '$mono500'}
          marginTop={'$2'}
          testID={messageId ? `customer-chat-message-time-${messageId}` : undefined}
          textAlign={position === 'right' ? 'right' : 'left'}
        >
          {formatChatTimestamp(createdAt)}
        </BodyS>
      ) : null}
    </YStack>
  );
};

type ConversationChatViewProps = {
  conversation: Conversation | null;
  emptyMessage: string;
  hideSenderIdentity?: boolean;
  inlineTitle?: string;
  onSendMessage?: (message: string) => Promise<void>;
  placeholder?: string;
  readOnly?: boolean;
  avatarName?: string;
};

const ConversationChatView = ({
  conversation,
  emptyMessage,
  hideSenderIdentity = false,
  inlineTitle,
  onSendMessage,
  placeholder = 'Message',
  readOnly = false,
  avatarName,
}: ConversationChatViewProps) => {
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [sending, setSending] = useState(false);
  const [paginator, setPaginator] = useState<Paginator<Message> | null>(null);
  const isFocused = useIsFocused();
  const lockChat = useChatStore(
    (state) => state.lockChat,
  );
  const { data: currentUser } = useGetCurrentEndUser();

  const composerDisabled = readOnly || lockChat || sending;
  const activeUserId = useMemo(
    () => `END_USER:${currentUser?.data.id ?? ''}`,
    [currentUser?.data.id],
  );

  const displayMessages = useMemo(() => {
    const attributes = getConversationAttributes(conversation);

    if (attributes.status !== 'resolved') {
      return messages;
    }

    const resolvedMessage: IMessage = {
      _id: RESOLVED_MESSAGE_ID,
      text: 'Marked as resolved',
      system: true,
      createdAt: messages[0]?.createdAt ?? new Date(),
      user: { _id: 'system' },
    };

    return [resolvedMessage, ...messages];
  }, [conversation, messages]);

  useEffect(() => {
    let isMounted = true;

    const loadMessages = async () => {
      if (!conversation) {
        setMessages([]);

        setPaginator(null);

        setLoading(false);

        return;
      }

      setLoading(true);

      try {
        const nextPaginator = await conversation.getMessages(PAGE_SIZE);

        if (!isMounted) {
          return;
        }

        setPaginator(nextPaginator);

        setMessages(
          mergeMessages(
            nextPaginator.items
              .map((message) => toGiftedChatMessage(message, conversation))
              .filter((message): message is IMessage => Boolean(message)),
          ),
        );
      } catch {
        if (isMounted) {
          setMessages([]);

          setPaginator(null);

          Toast.showErrorToast({
            text1: 'Could not load messages',
            text2: 'Please try again.',
          });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadMessages().catch(() => undefined);

    return () => {
      isMounted = false;
    };
  }, [conversation]);

  useEffect(() => {
    if (!isFocused) {
      return;
    }

    markConversationRead(conversation).catch(() => undefined);
  }, [conversation, isFocused]);

  useEffect(() => {
    if (!conversation) {
      return;
    }

    const handleMessageAdded = (message: Message) => {
      const nextMessage = toGiftedChatMessage(message, conversation);

      if (!nextMessage) {
        return;
      }

      setMessages((currentMessages) => {
        if (
          currentMessages.some(
            (currentMessage) => currentMessage._id === nextMessage._id,
          )
        ) {
          return currentMessages;
        }

        return mergeMessages([nextMessage, ...currentMessages]);
      });

      if (isFocused) {
        markConversationRead(conversation).catch(() => undefined);
      }
    };

    conversation.on('messageAdded', handleMessageAdded);

    return () => {
      conversation.removeListener('messageAdded', handleMessageAdded);
    };
  }, [conversation, isFocused]);

  const onLoadEarlier = useCallback(async () => {
    if (!paginator?.hasNextPage) {
      return;
    }

    setLoadingEarlier(true);

    try {
      const nextPaginator = await paginator.nextPage();

      setPaginator(nextPaginator);

      setMessages((currentMessages) =>
        mergeMessages([
          ...currentMessages,
          ...nextPaginator.items
            .map((message) => toGiftedChatMessage(message, conversation))
            .filter((message): message is IMessage => Boolean(message)),
        ]),
      );
    } catch {
      Toast.showErrorToast({
        text1: 'Could not load earlier messages',
        text2: 'Please try again.',
      });
    } finally {
      setLoadingEarlier(false);
    }
  }, [conversation, paginator]);

  const onSend = useCallback(
    async (nextMessages: IMessage[] = []) => {
      const nextMessage = nextMessages[0]?.text?.trim();

      if (!conversation || !nextMessage || !onSendMessage) {
        return;
      }

      setSending(true);

      try {
        await onSendMessage(nextMessage);
      } catch {
        Toast.showErrorToast({
          text1: 'Could not send message',
          text2: 'Please try again.',
        });
      } finally {
        setSending(false);
      }
    },
    [conversation, onSendMessage],
  );

  if (loading) {
    return (
      <YStack alignItems={'center'} flex={1} justifyContent={'center'}>
        <ActivityIndicator size={'large'} />
      </YStack>
    );
  }

  if (!conversation) {
    return (
      <YStack alignItems={'center'} flex={1} gap={'$2'} justifyContent={'center'} padding={'$6'}>
        {inlineTitle ? (
          <LabelM color={'$mono900'} fontWeight={'$2'}>
            {inlineTitle}
          </LabelM>
        ) : null}
        <BodyS color={'$mono500'} textAlign={'center'}>
          {emptyMessage}
        </BodyS>
      </YStack>
    );
  }

  return (
    <YStack
      data-conversation-unique-name={conversation.uniqueName ?? conversation.sid}
      flex={1}
      testID={'customer-active-chat-panel'}
    >
      {inlineTitle ? (
        <YStack
          backgroundColor={'$mono100'}
          borderBottomColor={'$mono300'}
          borderBottomWidth={1}
          paddingHorizontal={'$4'}
          paddingVertical={'$3'}
        >
          <LabelM color={'$mono900'} fontWeight={'$2'}>
            {inlineTitle}
          </LabelM>
        </YStack>
      ) : null}

      <GiftedChat
        alwaysShowSend={!composerDisabled}
        loadEarlier={paginator?.hasNextPage ?? false}
        messages={displayMessages}
        onLoadEarlier={onLoadEarlier}
        onSend={onSend}
        renderAvatar={
          hideSenderIdentity
            ? () => null
            : (props) => GenericChatAvatar(props, avatarName)
        }
        renderBubble={GenericChatBubble}
        renderDay={() => null}
        renderInputToolbar={(props) =>
          readOnly ? null : <ChatInput {...props} />
        }
        renderLoading={() => <ActivityIndicator size={'small'} />}
        renderSend={(props) =>
          composerDisabled ? null : <ChatSendButton {...props} />
        }
        renderSystemMessage={ResolvedSystemMessage}
        renderUsername={() => null}
        textInputProps={{
          editable: !composerDisabled,
          placeholder: lockChat ? 'Messaging is currently unavailable.' : placeholder,
          style: {
            fontFamily: CHAT_FONT_FAMILY,
          },
        }}
        user={{
          _id: activeUserId,
          name: activeUserId,
        }}
        isLoadingEarlier={loadingEarlier}
      />
    </YStack>
  );
};

export { ConversationChatView };
